/**
 * Utilities for JWT token decoding and comparison logic used in shadow invocation feature
 */

import { fireEvent } from '../../../analytics/useForgeUiAnalyticsEvent';

export interface DecodedJwtPayload {
	[key: string]: any;
	context?: Record<string, any>;
}

export interface TokenComparisonResult {
	mismatchedValues: Array<{ key: string; regular: any; shadow: any }>;
	onlyInRegular: string[];
	onlyInShadow: string[];
}

export interface InvocationResponse {
	[key: string]: any;
	contextToken?: {
		jwt?: string;
	};
}

/**
 * Decodes a JWT payload from a JWT string
 * @param jwt - The JWT token string
 * @returns The decoded payload or undefined if decoding fails
 */
export function decodeJwtPayload(jwt?: string): DecodedJwtPayload | undefined {
	if (!jwt) {
		return undefined;
	}

	try {
		const payload = jwt.split('.')[1];
		if (!payload) {
			return undefined;
		}

		const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
		const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

		return JSON.parse(atob(padded));
	} catch (e) {
		return undefined;
	}
}

/**
 * Checks if a value is a plain object (not array, null, or primitive)
 * @param value - The value to check
 * @returns True if the value is a plain object
 */
function isPlainObject(value: any): boolean {
	return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Recursively compares two objects and returns the differences
 * @param shadow - The shadow object to compare
 * @param regular - The regular object to compare
 * @param path - The current path in the object hierarchy
 * @param result - The result object to accumulate differences
 */
function visitObjectDifferences(
	shadow: any,
	regular: any,
	path: string[],
	result: TokenComparisonResult,
): void {
	const sObj = isPlainObject(shadow) ? shadow : {};
	const rObj = isPlainObject(regular) ? regular : {};
	const sKeys = new Set(Object.keys(sObj));
	const rKeys = new Set(Object.keys(rObj));

	// Find keys only in shadow
	sKeys.forEach((k) => {
		if (!rKeys.has(k)) {
			result.onlyInShadow.push([...path, k].join('.'));
		}
	});

	// Find keys only in regular
	rKeys.forEach((k) => {
		if (!sKeys.has(k)) {
			result.onlyInRegular.push([...path, k].join('.'));
		}
	});

	// Compare common keys
	sKeys.forEach((k) => {
		if (!rKeys.has(k)) {
			return;
		}

		const sv = sObj[k];
		const rv = rObj[k];

		if (isPlainObject(sv) && isPlainObject(rv)) {
			visitObjectDifferences(sv, rv, [...path, k], result);
		} else if (JSON.stringify(sv) !== JSON.stringify(rv)) {
			result.mismatchedValues.push({
				key: [...path, k].join('.'),
				shadow: sv,
				regular: rv,
			});
		}
	});
}

/**
 * Compares the context objects from two decoded JWT tokens
 * @param shadowToken - The decoded shadow token
 * @param regularToken - The decoded regular token
 * @returns Object containing the differences between the two contexts
 */
export function compareTokenContexts(
	shadowToken: DecodedJwtPayload | undefined,
	regularToken: DecodedJwtPayload | undefined,
): TokenComparisonResult {
	const shadow: any = shadowToken?.context || {};
	const regular: any = regularToken?.context || {};

	const result: TokenComparisonResult = {
		onlyInShadow: [],
		onlyInRegular: [],
		mismatchedValues: [],
	};

	visitObjectDifferences(shadow, regular, [], result);
	return result;
}

/**
 * Gets a flat list of all keys that have differences between tokens
 * (present in only one token or having different values)
 * @param differences - The result of comparing token contexts
 * @returns Array of all differing keys, sorted alphabetically
 */
export function getDifferingKeys(differences: TokenComparisonResult): string[] {
	const allDifferingKeys = new Set<string>();

	// Add keys that are only in shadow token
	differences.onlyInShadow.forEach((key) => allDifferingKeys.add(key));

	// Add keys that are only in regular token
	differences.onlyInRegular.forEach((key) => allDifferingKeys.add(key));

	// Add keys that have mismatched values
	differences.mismatchedValues.forEach((mismatch) => allDifferingKeys.add(mismatch.key));

	// Return sorted array for consistent output
	return Array.from(allDifferingKeys).sort();
}

/**
 * Gets detailed information about differing keys with their actual values
 * @param differences - The result of comparing token contexts
 * @returns Object mapping each differing key to its values in both tokens
 */
export function getDifferingKeysWithValues(
	differences: TokenComparisonResult,
): Record<string, { regular?: any; shadow?: any }> {
	const keyValues: Record<string, { regular?: any; shadow?: any }> = {};

	// Keys only in shadow token
	differences.onlyInShadow.forEach((key) => {
		keyValues[key] = { shadow: 'present', regular: undefined };
	});

	// Keys only in regular token
	differences.onlyInRegular.forEach((key) => {
		keyValues[key] = { regular: 'present', shadow: undefined };
	});

	// Keys with mismatched values
	differences.mismatchedValues.forEach((mismatch) => {
		keyValues[mismatch.key] = {
			regular: mismatch.regular,
			shadow: mismatch.shadow,
		};
	});

	return keyValues;
}

/**
 * Compares entire decoded JWT tokens (not just contexts)
 * @param shadowToken - The decoded shadow token
 * @param regularToken - The decoded regular token
 * @returns Object containing the differences between the two tokens
 */
export function compareEntireTokens(
	shadowToken: DecodedJwtPayload | undefined,
	regularToken: DecodedJwtPayload | undefined,
): TokenComparisonResult {
	const shadow: any = shadowToken || {};
	const regular: any = regularToken || {};

	const result: TokenComparisonResult = {
		onlyInShadow: [],
		onlyInRegular: [],
		mismatchedValues: [],
	};

	visitObjectDifferences(shadow, regular, [], result);
	return result;
}

/**
 * Determines whether to use the shadow response based on token context differences
 * @param differences - The result of comparing token contexts
 * @param regularToken - The decoded regular token for additional validation
 * @returns True if the shadow response should be used
 */
export function shouldUseShadowResponse(
	differences: TokenComparisonResult,
	regularToken: DecodedJwtPayload | undefined,
): boolean {
	const mismatchesEmpty = differences.mismatchedValues.length === 0;
	const onlyInRegularOk = differences.onlyInRegular.length <= 1;

	if (!mismatchesEmpty || !onlyInRegularOk) {
		return false;
	}

	if (differences.onlyInRegular.length === 1) {
		const onlyKey = differences.onlyInRegular[0];
		// When using context-only paths, this should be 'extension.content.subtype'
		const regularCtx: any = regularToken?.context || {};
		const regularSubtype = regularCtx?.extension?.content?.subtype;
		return onlyKey === 'extension.content.subtype' && regularSubtype === null;
	}

	// No differences inside context
	return true;
}

/**
 * Creates analytics payload for context token comparison
 * @param differences - The result of comparing token contexts
 * @param useShadow - Whether the shadow response should be used
 * @param regularResponse - The regular invocation response
 * @param shadowResponse - The shadow invocation response
 * @returns Analytics payload for the comparison event
 */
export function createTokenComparisonAnalyticsPayload(
	differences: TokenComparisonResult,
	useShadow: boolean,
	regularResponse: InvocationResponse,
	shadowResponse: InvocationResponse,
): {
	action: 'contextTokenComparison';
	actionSubject: 'forge.ui.invocation';
	attributes: {
		comparisonResult: string;
		differenceCount: number;
		differingKeys: string[];
		differingKeysWithValues: Record<
			string,
			{
				regular?: any;
				shadow?: any;
			}
		>;
		extensionId: string | undefined;
		extensionType: string | undefined;
		hasDifferences: boolean;
		mismatchedValuesCount: number;
		onlyInRegularCount: number;
		onlyInShadowCount: number;
		useShadowResponse: boolean;
	};
	source: string;
} {
	const differingKeys = getDifferingKeys(differences);
	const differingKeysWithValues = getDifferingKeysWithValues(differences);
	const hasDifferences = differingKeys.length > 0;

	return {
		action: 'contextTokenComparison' as const,
		actionSubject: 'forge.ui.invocation' as const,
		source: 'forge.ui.iframe',
		attributes: {
			comparisonResult: hasDifferences ? 'different' : 'same',
			hasDifferences,
			differenceCount: differingKeys.length,
			differingKeys, // Flat array of actionable key paths
			differingKeysWithValues, // Detailed values for each differing key
			onlyInRegularCount: differences.onlyInRegular.length,
			onlyInShadowCount: differences.onlyInShadow.length,
			mismatchedValuesCount: differences.mismatchedValues.length,
			useShadowResponse: useShadow,
			extensionId: (regularResponse.extensionId || shadowResponse.extensionId) as
				| string
				| undefined,
			extensionType: (regularResponse.extensionType || shadowResponse.extensionType) as
				| string
				| undefined,
		},
	};
}

/**
 * Main function to compare invocation responses and determine which one to use
 * @param regularResponse - The regular invocation response
 * @param shadowResponse - The shadow invocation response
 * @param createAnalyticsEvent - Function to create analytics events
 * @returns The response that should be used (either regular or shadow)
 */
export function selectOptimalInvocationResponse(
	regularResponse: InvocationResponse | undefined,
	shadowResponse: InvocationResponse | undefined,
	createAnalyticsEvent?: (event: any) => any,
): InvocationResponse | undefined {
	if (!shadowResponse || !regularResponse) {
		return regularResponse;
	}

	const decodedTokens = {
		shadow: decodeJwtPayload(shadowResponse.contextToken?.jwt),
		regular: decodeJwtPayload(regularResponse.contextToken?.jwt),
	};

	// Use context-only comparison for the decision logic (existing behavior)
	const contextDifferences = compareTokenContexts(decodedTokens.shadow, decodedTokens.regular);
	const useShadow = shouldUseShadowResponse(contextDifferences, decodedTokens.regular);

	if (createAnalyticsEvent) {
		// Use full token comparison for comprehensive analytics
		const fullDifferences = compareEntireTokens(decodedTokens.shadow, decodedTokens.regular);
		const analyticsPayload = createTokenComparisonAnalyticsPayload(
			fullDifferences,
			useShadow,
			regularResponse,
			shadowResponse,
		);
		fireEvent(createAnalyticsEvent, analyticsPayload);
	}

	return useShadow ? shadowResponse : regularResponse;
}
